import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Clock, User, FileText } from 'lucide-react';
import { getOrderHistory } from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateTimeWord } from '@/lib/dateFormat';

const OrderHistoryTab = ({ orderId }) => {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchHistory();
  }, [orderId]);

  useEffect(() => {
    // Filter history based on search term
    if (!searchTerm) {
      setFilteredHistory(history);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = history.filter((log) => {
        // Search in user name, action, field names, or values
        const searchableText = [
          log.changed_by_name,
          log.action,
          ...log.changes.map((c) => `${c.field_display} ${c.old_value} ${c.new_value}`),
        ]
          .join(' ')
          .toLowerCase();
        return searchableText.includes(term);
      });
      setFilteredHistory(filtered);
    }
  }, [searchTerm, history]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await getOrderHistory(orderId);
      setHistory(response.data || []);
      setFilteredHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch order history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const config = {
      create: { label: 'Created', variant: 'success', color: 'bg-green-100 text-green-700' },
      update: { label: 'Updated', variant: 'default', color: 'bg-blue-100 text-blue-700' },
      delete: { label: 'Deleted', variant: 'destructive', color: 'bg-red-100 text-red-700' },
    };
    return config[action] || config.update;
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return `Today, ${format(date, 'h:mm a')}`;
      } else if (diffInHours < 48) {
        return `Yesterday, ${format(date, 'h:mm a')}`;
      } else {
        return formatDateTimeWord(date);
      }
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No history recorded yet for this order.</p>
            <p className="text-sm mt-2">Changes will appear here once you start making updates.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search history (user, field, value...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Change History
            <Badge variant="outline" className="ml-auto">
              {filteredHistory.length} {filteredHistory.length === 1 ? 'entry' : 'entries'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No results found for &quot;{searchTerm}&quot;</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredHistory.map((log, index) => {
                const actionConfig = getActionBadge(log.action);
                const isLast = index === filteredHistory.length - 1;

                return (
                  <div key={log.log_id} className="relative">
                    {/* Timeline line */}
                    {!isLast && (
                      <div className="absolute left-4 top-10 bottom-0 w-px bg-border" />
                    )}

                    {/* Log entry */}
                    <div className="flex gap-4">
                      {/* Timeline dot */}
                      <div className="relative">
                        <div className={`w-8 h-8 rounded-full ${actionConfig.color} flex items-center justify-center z-10`}>
                          <User className="h-4 w-4" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 pb-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{log.changed_by_name}</span>
                              <Badge className={actionConfig.color} variant="outline">
                                {actionConfig.label}
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(log.timestamp)}
                              {log.changed_by_role && (
                                <>
                                  <span>•</span>
                                  <span className="capitalize">{log.changed_by_role}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Changes */}
                        {log.changes && log.changes.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {log.changes.map((change, idx) => (
                              <div
                                key={idx}
                                className="bg-muted/50 rounded-lg p-3 text-sm"
                              >
                                <div className="font-medium text-foreground mb-1">
                                  {change.field_display}
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <span className="line-through">{change.old_value}</span>
                                  <span>→</span>
                                  <span className="text-foreground font-medium">
                                    {change.new_value}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Create/Delete message */}
                        {log.action === 'create' && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            Order was created
                          </div>
                        )}
                        {log.action === 'delete' && (
                          <div className="mt-2 text-sm text-muted-foreground">
                            Order was deleted
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderHistoryTab;
